import { format, parseISO, isValid } from 'date-fns';

/**
 * Data Transformation Utilities
 * Centralized data transformation patterns to eliminate duplication
 */

// ============================================================================
// DATE TRANSFORMATIONS
// ============================================================================

/**
 * Format date with fallback
 */
export function formatDate(
  date: string | Date | null | undefined,
  formatString: string = 'MMM d, yyyy',
  fallback: string = 'N/A'
): string {
  if (!date) return fallback;

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? format(dateObj, formatString) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Format date range
 */
export function formatDateRange(
  startDate: string | Date | null,
  endDate: string | Date | null,
  formatString: string = 'MMM d, yyyy'
): string {
  const start = formatDate(startDate, formatString, '');
  const end = formatDate(endDate, formatString, '');

  if (!start && !end) return 'N/A';
  if (!start) return `Until ${end}`;
  if (!end) return `From ${start}`;

  return `${start} - ${end}`;
}

/**
 * Format relative time
 */
export function formatRelativeTime(date: string | Date | null): string {
  if (!date) return 'Never';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

    return `${Math.floor(diffDays / 365)} years ago`;
  } catch {
    return 'Never';
  }
}

// ============================================================================
// MONEY TRANSFORMATIONS
// ============================================================================

/**
 * Format percentage
 */
export function formatPercentage(
  value: number | string | null,
  decimals: number = 0
): string {
  if (value === null || value === undefined) return '0%';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) return '0%';

  return `${numValue.toFixed(decimals)}%`;
}

// ============================================================================
// STRING TRANSFORMATIONS
// ============================================================================

/**
 * Truncate text with ellipsis
 */
export function truncateText(
  text: string | null | undefined,
  maxLength: number = 50,
  suffix: string = '...'
): string {
  if (!text) return '';

  if (text.length <= maxLength) return text;

  return text.substring(0, maxLength - suffix.length) + suffix;
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return phone; // Return original if can't format
}

/**
 * Format name (proper case)
 */
export function formatName(name: string | null | undefined): string {
  if (!name) return '';

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format email (lowercase and trim)
 */
export function formatEmail(email: string | null | undefined): string {
  if (!email) return '';
  return email.toLowerCase().trim();
}

/**
 * Extract initials from name
 */
export function getInitials(
  name: string | null | undefined,
  maxChars: number = 2
): string {
  if (!name) return '';

  const words = name.split(' ').filter((word) => word.length > 0);
  const initials = words.map((word) => word[0].toUpperCase()).join('');

  return initials.slice(0, maxChars);
}

// ============================================================================
// ARRAY TRANSFORMATIONS
// ============================================================================

/**
 * Group array by key
 */
export function groupBy<T>(
  array: T[],
  keyGetter: (item: T) => string
): Record<string, T[]> {
  return array.reduce(
    (groups, item) => {
      const key = keyGetter(item);
      groups[key] = groups[key] || [];
      groups[key].push(item);
      return groups;
    },
    {} as Record<string, T[]>
  );
}

/**
 * Sort by multiple fields
 */
export function sortBy<T>(
  array: T[],
  ...sortFns: Array<(a: T, b: T) => number>
): T[] {
  return [...array].sort((a, b) => {
    for (const fn of sortFns) {
      const result = fn(a, b);
      if (result !== 0) return result;
    }
    return 0;
  });
}

/**
 * Paginate array
 */
export function paginate<T>(
  array: T[],
  page: number = 1,
  limit: number = 10
): {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
} {
  const start = (page - 1) * limit;
  const end = start + limit;
  const data = array.slice(start, end);
  const total = array.length;
  const totalPages = Math.ceil(total / limit);

  return {
    data,
    total,
    page,
    limit,
    totalPages,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1
  };
}

// ============================================================================
// STATUS TRANSFORMATIONS
// ============================================================================

/**
 * Get human-readable status label
 */
export const statusLabels = {
  // Task statuses
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',

  // Processing statuses
  pending: 'Pending',
  processing: 'Processing',
  failed: 'Failed',

  // General statuses
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
  draft: 'Draft',
  published: 'Published'
} as const;

export function formatStatus(status: string | null | undefined): string {
  if (!status) return 'Unknown';

  return (
    statusLabels[status as keyof typeof statusLabels] ||
    status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

/**
 * Get priority label
 */
export const priorityLabels = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent'
} as const;

export function formatPriority(priority: string | null | undefined): string {
  if (!priority) return 'Medium';

  return (
    priorityLabels[priority as keyof typeof priorityLabels] ||
    priority.replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

// ============================================================================
// FILE TRANSFORMATIONS
// ============================================================================

/**
 * Format file size
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return '0 Bytes';

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}

/**
 * Get file extension
 */
export function getFileExtension(filename: string | null | undefined): string {
  if (!filename) return '';

  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Get file type from mime type
 */
export function getFileType(mimeType: string | null | undefined): string {
  if (!mimeType) return 'Unknown';

  const typeMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'Word Document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'Word Document',
    'image/jpeg': 'JPEG Image',
    'image/png': 'PNG Image',
    'image/gif': 'GIF Image',
    'text/plain': 'Text File',
    'text/csv': 'CSV File',
    'application/json': 'JSON File',
    'application/xml': 'XML File'
  };

  return (
    typeMap[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'Unknown'
  );
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Safe parse JSON
 */
export function safeParseJSON<T = unknown>(
  json: string | null | undefined,
  fallback: T
): T {
  if (!json) return fallback;

  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/**
 * Check if value is empty
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Get nested property safely
 */
export function get<T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  path: string,
  defaultValue?: T
): T {
  const keys = path.split('.');
  let result: unknown = obj;

  for (const key of keys) {
    if (result === null || result === undefined) {
      return defaultValue as T;
    }
    result = (result as Record<string, unknown>)[key];
  }

  if (result === undefined) return defaultValue as T;
  return result as T;
}
